import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { LinkTypeEnum } from '../../../Forms/enums/link-type.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { LinkModel } from '../../../Forms/models/link.model';
import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { validateWebsiteURL } from '../../../Misc/helpers/formatting/url.helper';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

@Component({
    selector: 'agpr-link-dialog',
    templateUrl: './link-dialog.component.html',
    styleUrls: ['./link-dialog.component.scss'],
    standalone: false
})
export class LinkDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string = '';
    @Input() link: LinkModel;
    @Input() availableLinkTypes: DropdownOptionModel[];
    @Input() heading: string;
    @Input() isLinkField: boolean = false;
    @Input() enableTooltip?: boolean = true;

    @Output() removeLink: EventEmitter<void> = new EventEmitter();

    selectedLinkType: DropdownOptionModel;
    linkTypeEnum = LinkTypeEnum;
    messageSeverityEnum = messageSeverityEnum;

    url: string;
    tooltip: string;
    openInNewWindow: boolean;

    email: string;
    body: string;
    subject: string;
    mailTo: string;

    emailValidationError: boolean = false;
    urlValidationError: boolean = false;

    constructor(
        coreDialogService: CoreDialogService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, 'Link');
    }

    ngOnInit() {
        this.availableLinkTypes.forEach(type => type.label = this.localizationService.transform(type.label));
        let initialSelectionIndex = -1;
        if (!this.link || !this.link.type) {
            initialSelectionIndex = this.availableLinkTypes.findIndex(option => option.value === LinkTypeEnum.URL);
            if (initialSelectionIndex > -1) {
                this.selectedLinkType = this.availableLinkTypes[initialSelectionIndex];
            }

            return;
        }

        initialSelectionIndex = this.availableLinkTypes.findIndex(option => option.value === this.link.type);
        if (initialSelectionIndex > -1) {
            this.selectedLinkType = this.availableLinkTypes[initialSelectionIndex];
        }

        if (this.link.type === LinkTypeEnum.URL) {
            this.url = this.link.url;
            this.openInNewWindow = this.link.openInNewWindow;
            this.tooltip = this.link.tooltip;
        } else if (this.link.type === LinkTypeEnum.EMAIL) {
            this.email = this.link.email;
            this.body = this.link.body;
            this.subject = this.link.subject;
        }
    }

    cancel() {
        this.closeDialog();
    }

    removeLinkFromContext() {
        this.removeLink.emit();
        this.closeDialog();
    }

    validate(): void {
        if (this.selectedLinkType.value === LinkTypeEnum.URL) {
            if (!this.url) {
                return;
            }
            this.urlValidationError = !validateWebsiteURL(this.url);
        } else if (this.selectedLinkType.value === LinkTypeEnum.EMAIL) {
            this.emailValidationError = !validateEmail(this.email);
        }
    }

    insertLink() {
        const link = new LinkModel();

        this.validate();

        if (this.selectedLinkType.value === LinkTypeEnum.URL) {
            if (this.urlValidationError) {
                return;
            }

            link.type = LinkTypeEnum.URL;
            link.url = this.url?.trim();
            link.tooltip = this.tooltip;
            link.openInNewWindow = this.openInNewWindow;
        } else if (this.selectedLinkType.value === LinkTypeEnum.EMAIL) {
            if (this.emailValidationError) {
                return;
            }

            link.type = LinkTypeEnum.EMAIL;
            link.email = this.email;
            link.subject = this.subject;
            link.body = this.body;
            link.mailTo = link.encodeMailTo()?.trim();
        }

        this.closeDialog(link);
    }
}
